import React, { useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import SafetyWorkflowStepper from '../../components/safety/SafetyWorkflowStepper';
import type { SafetyWorkflowProgress } from '../../domain/safetyWorkflowStepMapping';
import {
  displaySafetyManagerLabel,
  resolveSafetyManagerHelperForDisplayLabel,
} from '../../domain/safetyManagerUxLabels';
import ProofSummaryAdvancedSections from './ProofSummaryAdvancedSections';
import {
  buildEvidenceStrengthMeaning,
  isLowEvidenceStrengthLabel,
  SAFETY_REPORT_ADVANCED_DETAILS_SUBTITLE,
  SAFETY_REPORT_ADVANCED_DETAILS_TITLE,
  SAFETY_REPORT_LOW_EVIDENCE_NOTE,
  SAFETY_REPORT_NO_RECURRING_RISKS,
  type SafetyReportTopStatusPanel,
} from './safetyReportUxCopy';
import InvestigationAlignmentCard from '../../components/safetyReports/InvestigationAlignmentCard';
import ClaimsRoiAlignmentCard from '../../components/safetyReports/ClaimsRoiAlignmentCard';
import SafetyControlReviewCard from '../../components/safetyReports/SafetyControlReviewCard';
import type { InvestigationAlignmentViewModel } from '../../domain/reportContext/investigationAlignment';
import type { ClaimsRoiAlignmentViewModel } from '../../domain/reportContext/claimsRoiAlignment';
import type { SafetyControlReviewViewModel } from '../../domain/reportContext/safetyControlReview';

type Fact = { label: string; value: string };
type CountRow = { count: number; [key: string]: unknown };

function TopStatusPanel({ panel }: { panel: SafetyReportTopStatusPanel }) {
  const rows: Array<{ label: string; value: string }> = [
    { label: 'Status', value: panel.status },
    { label: 'Next step', value: panel.nextStep },
    { label: 'Missing', value: panel.missing },
  ];
  return (
    <Stack spacing={1.25} data-testid="safety-report-top-status-panel">
      {rows.map((row) => (
        <Box key={row.label} sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.02 }}>
            {row.label}
          </Typography>
          <Typography
            variant={row.label === 'Next step' ? 'subtitle1' : 'body2'}
            sx={{
              mt: 0.35,
              fontWeight: row.label === 'Next step' ? 800 : 700,
              lineHeight: 1.45,
              color: row.label === 'Next step' ? 'text.primary' : 'text.secondary',
            }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function SecondaryStatusFactGrid({ facts }: { facts: Fact[] }) {
  if (facts.length === 0) return null;
  return (
    <Box
      sx={{
        mt: 1,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        gap: 1,
      }}
      data-testid="safety-report-secondary-status-facts"
    >
      {facts.map((fact) => {
        const helper = resolveSafetyManagerHelperForDisplayLabel(fact.label);
        return (
          <Box key={fact.label} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.02 }}>
              {fact.label}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600, lineHeight: 1.45 }}>
              {fact.value}
            </Typography>
            {helper ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.45 }}>
                {helper}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

export type ProofSummarySurfaceSection = 'summary' | 'details' | 'all';

type Props = {
  section?: ProofSummarySurfaceSection;
  sectionRef?: (node: HTMLDivElement | null) => void;
  customerSummarySentence: string;
  investigationAlignment?: InvestigationAlignmentViewModel | null;
  claimsRoiAlignment?: ClaimsRoiAlignmentViewModel | null;
  safetyControlReview?: SafetyControlReviewViewModel | null;
  customerTopStatusPanel: SafetyReportTopStatusPanel;
  customerSecondaryStatusFacts: Fact[];
  customerWhatHappened: string;
  customerRiskPatternSummary: string;
  customerRiskPatternFacts: Fact[];
  customerEvidencePoints: string[];
  customerReviewStatusSummary: string;
  customerReviewChecklist: string[];
  customerReviewOutcomeSummary: string;
  customerReviewTrendSummary?: string;
  customerReviewBoundarySummary?: string;
  customerReviewOutcomeRows: CountRow[];
  customerOwnershipSummary: string;
  customerOwnershipDetailFacts: Fact[];
  customerOwnershipRows: CountRow[];
  customerEscalationSummary: string;
  customerEscalationRows: CountRow[];
  customerCorrectiveActionSummary: string;
  customerCorrectiveActionRows: CountRow[];
  customerMeasurementSummary: string;
  customerMeasurementBoundarySummary?: string;
  customerMeasurementRows: CountRow[];
  customerActionSnapshotSummary: string;
  customerActionSnapshotRows: CountRow[];
  customerFollowupFacts: Fact[];
  customerUnprovenSummary: string;
  reportBuilding?: boolean;
  reportBuildingBannerText?: string;
  reportBuildingScopeSummary?: string;
  showReportContent?: boolean;
  showPreviousReportBanner?: boolean;
  showIncompleteReportBanner?: boolean;
  onPrimaryIncidentEvidencePackageAction: () => void;
  primaryIncidentEvidencePackageLabel: string;
  primaryIncidentEvidencePackageHelper?: string;
  evidenceBundleCreating: boolean;
  evidenceBundleDownloading?: boolean;
  canOperateOnProofPackage: boolean;
  reportReadinessActionLabel: string;
  reportReadinessActionHelper?: string;
  reportReadinessActionDisabled?: boolean;
  onReportReadinessAction: () => void;
  onShowTechnicalDetails: () => void;
  safetyWorkflowProgress?: SafetyWorkflowProgress | null;
  executiveSummaryText?: string;
  findingText?: string;
  evidenceReadinessLabel?: string;
  evidenceReadinessDetail?: string;
  showActionButtons?: boolean;
  showAdvancedDetails?: boolean;
};

export default function ProofSummarySurface({
  section = 'all',
  sectionRef,
  customerSummarySentence,
  investigationAlignment = null,
  claimsRoiAlignment = null,
  safetyControlReview = null,
  customerTopStatusPanel,
  customerSecondaryStatusFacts,
  customerWhatHappened,
  customerRiskPatternSummary,
  customerRiskPatternFacts,
  customerEvidencePoints,
  customerReviewStatusSummary,
  customerReviewChecklist,
  customerReviewOutcomeSummary,
  customerReviewTrendSummary,
  customerReviewBoundarySummary,
  customerReviewOutcomeRows,
  customerOwnershipSummary,
  customerOwnershipDetailFacts,
  customerOwnershipRows,
  customerEscalationSummary,
  customerEscalationRows,
  customerCorrectiveActionSummary,
  customerCorrectiveActionRows,
  customerMeasurementSummary,
  customerMeasurementBoundarySummary,
  customerMeasurementRows,
  customerActionSnapshotSummary,
  customerActionSnapshotRows,
  customerFollowupFacts,
  customerUnprovenSummary,
  reportBuilding = false,
  reportBuildingBannerText = 'Building Safety Report ZIP package...',
  reportBuildingScopeSummary,
  showReportContent = true,
  showPreviousReportBanner = false,
  showIncompleteReportBanner = false,
  onPrimaryIncidentEvidencePackageAction,
  primaryIncidentEvidencePackageLabel,
  primaryIncidentEvidencePackageHelper,
  evidenceBundleCreating,
  evidenceBundleDownloading = false,
  canOperateOnProofPackage,
  reportReadinessActionLabel,
  reportReadinessActionHelper,
  reportReadinessActionDisabled = false,
  onReportReadinessAction,
  onShowTechnicalDetails,
  safetyWorkflowProgress = null,
  executiveSummaryText,
  findingText,
  evidenceReadinessLabel,
  evidenceReadinessDetail,
  showActionButtons = true,
  showAdvancedDetails = true,
}: Props) {
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const showSummary = section === 'all' || section === 'summary';
  const showDetails = section === 'all' || section === 'details';

  const evidenceStrengthFact = useMemo(
    () => customerSecondaryStatusFacts.find((fact) => fact.label === displaySafetyManagerLabel('evidence_quality')),
    [customerSecondaryStatusFacts],
  );
  const detectionBasisFact = useMemo(
    () => customerSecondaryStatusFacts.find((fact) => fact.label === displaySafetyManagerLabel('measurement_basis')),
    [customerSecondaryStatusFacts],
  );
  const evidenceStrengthMeaning = useMemo(
    () => buildEvidenceStrengthMeaning({
      strengthLabel: evidenceStrengthFact?.value || '',
      detectionBasisLabel: detectionBasisFact?.value,
    }),
    [detectionBasisFact?.value, evidenceStrengthFact?.value],
  );
  const showLowEvidenceAlert = isLowEvidenceStrengthLabel(evidenceStrengthFact?.value || '');

  const summaryCard = showSummary ? (
    <Paper
      variant="outlined"
      data-testid="safety-report-summary-card"
      sx={{
        p: { xs: 2, sm: 2.25 },
        borderRadius: 3,
        borderColor: 'primary.main',
        boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.16)',
      }}
    >
      <Stack spacing={1.75}>
        {reportBuilding ? (
          <Alert severity="info" data-testid="safety-report-building-banner">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {reportBuildingBannerText}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.35 }}>
              {`Using selected scope: ${reportBuildingScopeSummary || 'Selected report scope'}`}
            </Typography>
          </Alert>
        ) : null}
        {showPreviousReportBanner ? (
          <Alert severity="info">
            Showing last built report while the new report is being generated.
          </Alert>
        ) : null}
        {showIncompleteReportBanner ? (
          <Alert severity="warning">
            Report values may be incomplete until generation finishes.
          </Alert>
        ) : null}
        {showReportContent ? (
          <>
            {safetyWorkflowProgress ? (
              <Box sx={{ pb: 0.25 }}>
                <SafetyWorkflowStepper progress={safetyWorkflowProgress} />
              </Box>
            ) : null}
            <TopStatusPanel panel={customerTopStatusPanel} />
            <InvestigationAlignmentCard
              alignment={investigationAlignment}
              compact
              title="Investigation alignment"
              testId="proof-summary-investigation-alignment"
            />
            <ClaimsRoiAlignmentCard
              alignment={claimsRoiAlignment}
              compact
              title="Claims / ROI alignment"
              testId="proof-summary-claims-roi"
            />
            <SafetyControlReviewCard
              alignment={safetyControlReview}
              compact
              title="Safety control review"
              testId="proof-summary-safety-control-review"
            />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Report values will appear after generation finishes.
          </Typography>
        )}
      </Stack>
    </Paper>
  ) : null;

  if (!showDetails) {
    return summaryCard;
  }

  const actionButtons = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap" alignItems="flex-start">
      <Stack spacing={0.5} sx={{ minWidth: { sm: 220 } }}>
        <Button
          variant="contained"
          size="large"
          onClick={onPrimaryIncidentEvidencePackageAction}
          disabled={evidenceBundleCreating || evidenceBundleDownloading || !canOperateOnProofPackage}
          sx={{ fontWeight: 800, alignSelf: 'flex-start' }}
        >
          {primaryIncidentEvidencePackageLabel}
        </Button>
        {primaryIncidentEvidencePackageHelper ? (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, maxWidth: 480 }}>
            {primaryIncidentEvidencePackageHelper}
          </Typography>
        ) : null}
      </Stack>
      <Stack spacing={0.5}>
        <Button
          variant="outlined"
          onClick={onReportReadinessAction}
          disabled={reportReadinessActionDisabled}
        >
          {reportReadinessActionLabel}
        </Button>
        {reportReadinessActionHelper ? (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, maxWidth: 360 }}>
            {reportReadinessActionHelper}
          </Typography>
        ) : null}
      </Stack>
      <Button variant="outlined" onClick={onShowTechnicalDetails}>
        {SAFETY_REPORT_ADVANCED_DETAILS_TITLE}
      </Button>
    </Stack>
  );

  const detailSections = (
    <Stack spacing={2}>
      {showIncompleteReportBanner ? (
        <Alert severity="warning">
          Report values may be incomplete until generation finishes.
        </Alert>
      ) : null}
      {showPreviousReportBanner ? (
        <Alert severity="info">
          Showing last built report while the new report is being generated.
        </Alert>
      ) : null}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          Executive Summary
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.45, lineHeight: 1.5 }}>
          {executiveSummaryText || customerSummarySentence || customerWhatHappened}
        </Typography>
        {!executiveSummaryText && customerSummarySentence && customerWhatHappened !== customerSummarySentence ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.5 }}>
            {customerWhatHappened}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          Result
        </Typography>
        {!customerRiskPatternSummary.trim() && customerRiskPatternFacts.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.5 }}>
            {findingText || SAFETY_REPORT_NO_RECURRING_RISKS}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ mt: 0.45, lineHeight: 1.5 }}>
              {findingText || customerRiskPatternSummary}
            </Typography>
            {!findingText && customerRiskPatternFacts.length > 0 ? (
              <Box
                sx={{
                  mt: 1,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                  gap: 1,
                }}
              >
                {customerRiskPatternFacts.map((fact) => {
                  const helper = resolveSafetyManagerHelperForDisplayLabel(fact.label);
                  return (
                    <Paper key={fact.label} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.02 }}>
                        {fact.label}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ mt: 0.35, fontWeight: 700, lineHeight: 1.45 }}>
                        {fact.value}
                      </Typography>
                      {helper ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.45 }}>
                          {helper}
                        </Typography>
                      ) : null}
                    </Paper>
                  );
                })}
              </Box>
            ) : null}
          </>
        )}
      </Box>
      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }} data-testid="safety-report-evidence-strength">
        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          Evidence
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.45, lineHeight: 1.5 }}>
          {evidenceReadinessLabel || evidenceStrengthFact?.value || 'Not assessed yet'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.5 }}>
          {evidenceReadinessDetail || evidenceStrengthMeaning}
        </Typography>
        {!evidenceReadinessDetail ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.45 }}>
            {resolveSafetyManagerHelperForDisplayLabel(displaySafetyManagerLabel('evidence_quality'))}
          </Typography>
        ) : null}
        {!evidenceReadinessDetail && showLowEvidenceAlert ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {SAFETY_REPORT_LOW_EVIDENCE_NOTE}
          </Alert>
        ) : null}
        <SecondaryStatusFactGrid facts={customerSecondaryStatusFacts} />
      </Box>
      {showActionButtons ? actionButtons : null}
      {showAdvancedDetails ? (
        <Accordion
          expanded={advancedExpanded}
          onChange={(_, expanded) => setAdvancedExpanded(expanded)}
          disableGutters
          elevation={0}
          data-testid="safety-report-advanced-details"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {SAFETY_REPORT_ADVANCED_DETAILS_TITLE}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45, mt: 0.25 }}>
                {SAFETY_REPORT_ADVANCED_DETAILS_SUBTITLE}
              </Typography>
            </Box>
          </AccordionSummary>
          {advancedExpanded ? (
            <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
              <ProofSummaryAdvancedSections
                customerEvidencePoints={customerEvidencePoints}
                customerReviewStatusSummary={customerReviewStatusSummary}
                customerReviewChecklist={customerReviewChecklist}
                customerReviewOutcomeSummary={customerReviewOutcomeSummary}
                customerReviewTrendSummary={customerReviewTrendSummary}
                customerReviewBoundarySummary={customerReviewBoundarySummary}
                customerReviewOutcomeRows={customerReviewOutcomeRows}
                customerOwnershipSummary={customerOwnershipSummary}
                customerOwnershipDetailFacts={customerOwnershipDetailFacts}
                customerOwnershipRows={customerOwnershipRows}
                customerEscalationSummary={customerEscalationSummary}
                customerEscalationRows={customerEscalationRows}
                customerCorrectiveActionSummary={customerCorrectiveActionSummary}
                customerCorrectiveActionRows={customerCorrectiveActionRows}
                customerMeasurementSummary={customerMeasurementSummary}
                customerMeasurementBoundarySummary={customerMeasurementBoundarySummary}
                customerMeasurementRows={customerMeasurementRows}
                customerActionSnapshotSummary={customerActionSnapshotSummary}
                customerActionSnapshotRows={customerActionSnapshotRows}
                customerFollowupFacts={customerFollowupFacts}
                customerUnprovenSummary={customerUnprovenSummary}
              />
            </AccordionDetails>
          ) : null}
        </Accordion>
      ) : null}
    </Stack>
  );

  if (section === 'details') {
    return (
      <Paper
        ref={sectionRef}
        variant="outlined"
        data-testid="safety-report-detail-sections"
        sx={{ p: { xs: 1.5, sm: 1.75 }, borderRadius: 2.5 }}
      >
        {detailSections}
      </Paper>
    );
  }

  return (
    <>
      {summaryCard}
      <Paper
        ref={sectionRef}
        variant="outlined"
        data-testid="safety-report-detail-sections"
        sx={{ p: { xs: 2, sm: 2.25 }, borderRadius: 2.5, mt: 2.5 }}
      >
        {detailSections}
      </Paper>
    </>
  );
}
